import { getAppBasePath } from '@core/utils/assetFolderSlug';
import {
  normalizeSupportedLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@core/i18n/language';

/**
 * Utility functions for parsing game information from URLs.
 * Supports both path-based URLs (/gamename/demo) and query parameter URLs (?game=gamename)
 */

/** Strip Vite `base` (e.g. `/ps/crash`) so game slugs are parsed from the remainder. */
function getPathnameWithoutAppBase(): string {
  const pathname = window.location.pathname;
  const base = getAppBasePath();
  if (!base) {
    return pathname;
  }
  if (pathname === base) {
    return '/';
  }
  if (pathname.startsWith(`${base}/`)) {
    return pathname.slice(base.length) || '/';
  }
  return pathname;
}

export interface GameUrlInfo {
  game: string | null;
  isDemo: boolean;
}

/** Language codes supported in URL path or query (`?lang=`, `?language=`, `?locale=`). */
export const URL_LANGUAGE_CODES = SUPPORTED_LANGUAGES;
export type UrlLanguageCode = SupportedLanguage;

function normalizeLanguageCode(
  raw: string | null | undefined
): UrlLanguageCode | null {
  return normalizeSupportedLanguage(raw);
}
/**
 * Reads language from the current URL (query or first path segment after app base).
 * Examples: `?lang=en`, `?language=es`, `/en/skyx_new`, `/ps/crash/es/skyx_new/demo`
 */
export function parseLanguageFromUrl(): UrlLanguageCode | null {
  const searchParams = new URLSearchParams(window.location.search);
  for (const key of ['lang', 'language', 'locale']) {
    const fromQuery = normalizeLanguageCode(searchParams.get(key));
    if (fromQuery) return fromQuery;
  }

  const pathname = getPathnameWithoutAppBase();
  const pathParts = pathname.split('/').filter(part => part.length > 0);
  return normalizeLanguageCode(pathParts[0]);
}

function getPathPartsWithoutLanguage(): string[] {
  const pathname = getPathnameWithoutAppBase();
  const pathParts = pathname.split('/').filter(part => part.length > 0);
  if (normalizeLanguageCode(pathParts[0])) {
    return pathParts.slice(1);
  }
  return pathParts;
}

/**
 * Parses game name and demo flag from the current URL.
 * Supports:
 * - Path-based: /crash/demo, /aviator/demo
 * - Query-based: ?game=crash&demo=true
 * - Query-based: ?game=crash (demo=false)
 *
 * @returns Object with game name and demo flag
 */
export function parseGameFromUrl(): GameUrlInfo {
  const searchParams = new URLSearchParams(window.location.search);

  // First, check query parameters (for backwards compatibility)
  const gameFromQuery = searchParams.get('game');
  const demoFromQuery =
    searchParams.get('demo') === 'true' || searchParams.get('mode') === 'demo';

  if (gameFromQuery) {
    return {
      game: gameFromQuery,
      isDemo: demoFromQuery,
    };
  }

  // Then check path-based URLs: /gamename/demo or /gamename (optional /en/ prefix)
  const pathParts = getPathPartsWithoutLanguage();

  if (pathParts.length === 0) {
    return { game: null, isDemo: false };
  }

  // Check if last part is "demo"
  const lastPart = pathParts[pathParts.length - 1];
  const isDemoInPath = lastPart === 'demo';

  if (isDemoInPath && pathParts.length >= 2) {
    // Format: /gamename/demo
    const gameName = pathParts[pathParts.length - 2];
    return {
      game: gameName,
      isDemo: true,
    };
  } else if (!isDemoInPath) {
    // Format: /gamename (not demo)
    return {
      game: lastPart,
      isDemo: false,
    };
  }

  // Default: no game found
  return { game: null, isDemo: false };
}

/**
 * Gets the game name from URL (path or query)
 */
export function getGameFromUrl(): string | null {
  return parseGameFromUrl().game;
}

/**
 * Checks if demo mode is requested in the URL
 */
export function isDemoInUrl(): boolean {
  return parseGameFromUrl().isDemo;
}
